import useAxios from '@/composables/useAxios';
import { TaskConfigV2 as ApiTaskConfig } from '@/interfaces/TaskConfig';
import { ref } from 'vue';
import { AxiosResponse } from 'axios';
import Session from '@/models/Session';
import Serie from '@/models/Serie';
import TaskType from '@/models/TaskType';
import BasicTask from '@/models/Tasks/BasicTask';
import CalcTask from '@/models/Tasks/CalcTask';
import Task from '@/interfaces/Task';
// eslint-disable-next-line import/no-named-default
import { default as TaskModel } from '@/models/Task';
import { useRouter } from 'vue-router';
import SessionStatus from '@/models/SessionStatus';
import { PresenceChannel } from 'laravel-echo';
import Progress from '@/interfaces/Progress';
import ProgressStatus from '@/models/ProgressStatus';
import useElasticApmRumMetricsAdapter from '@/composables/v2/metrics/adapters/useElasticApmRumMetricsAdapter';
import useMetrics, { setObservedSessionId } from '@/composables/v2/metrics/useMetrics';
import useTaskProgress from '@/composables/v2/useTaskProgress';
import { createSessionEvent } from '@/models/Metrics/SessionEvent';
import SessionEventScope from '@/models/Metrics/SessionEventScope';
import SessionEventType from '@/models/Metrics/SessionEventType';
import { setStudentId } from '@/composables/v2/metrics/useAuthenticatedStudent';
import useSessionEvents from '@/composables/v2/metrics/useSessionEvents';
import { clearAllHeaderAudioProgress, clearHeaderAudioProgressOfOtherSessions } from '@/composables/v2/useHeaderAudio';
import FakeConfig from '../config/FakeConfig';

/** eprocom-pwa#857: the pull after a slow upload still gets a fair chance. */
const MIN_NEXT_TASK_PULL_TIMEOUT_MS = 10000;

// define the login task before the session is loaded from the API
const session = ref<null | Session>(new Session('', [new Serie([new BasicTask('pem-ide', 'LOGIN page')], 'PEM')]));
const taskConfig = ref<null | ApiTaskConfig>(null);
const fakeConfig = new FakeConfig();

// save the latest session status received from the broadcast channel
const currentSessionStatus = ref<null | SessionStatus>(null);
// save the latest progress status received from the broadcast channel
const currentProgressStatus = ref<null | ProgressStatus>(null);

// the currentSerie and currentTask variables are used to manage the navigation between task components
// these variables can differ from the session configuration because the user can join a session in progress
export const currentSerie = ref<Serie | null>();
export const currentTask = ref<TaskModel | null>();

/**
 * Set the current serie and task from the session configuration
 * The current serie and/or task can be different from the one in the session configuration
 * after the joinSession method is called when the user wants to recover its session status for example
 */
export function loadCurrentTaskAndSerie(): void {
    currentSerie.value = session.value?.getCurrentSerie();
    currentTask.value = session.value?.getCurrentTask();
}

export default function useSession() {
    const axios = useAxios();
    const router = useRouter();
    const { endTransaction, addLabels, currentTransaction } = useElasticApmRumMetricsAdapter();
    const { forceFlushSessionEvents, dispatchSessionEvents, flushSessionEventsIfCurrentSessionExist } = useMetrics();
    const { flushAllSessionEvents } = useSessionEvents();
    const { getTaskToDo, setCurrentTaskConfig } = useTaskProgress();

    /**
     * Converts an API task formatted in JSON to its related TaskModel
     */
    function mapApiTaskToTaskModel(apiTask: Task): TaskModel {
        if (apiTask.type === TaskType.BASIC_TASK) {
            return new BasicTask(apiTask.name, apiTask.name, apiTask.expectedResponseValue ?? undefined);
        }
        if (apiTask.type === TaskType.CALC_TASK) {
            if (!apiTask.calculus) {
                throw new Error(`CalcTask ${apiTask.name} has no calculus`);
            }
            return new CalcTask(apiTask.name, apiTask.description, apiTask.calculus);
        }

        throw new Error(`Unknown task type: ${apiTask.type}`);
    }

    /**
     * Converts an API task config formatted in JSON to its related Session model
     */
    function mapApiTaskConfigToSessionModel(apiTaskConfig: ApiTaskConfig): Session {
        const sessionConfig = new Session(
            apiTaskConfig.session_id,
            apiTaskConfig.series.map(
                (series) =>
                    new Serie(
                        series.tasks.map((task) => mapApiTaskToTaskModel(task)),
                        series.name
                    )
            )
        );

        let currSerie: Serie | null = sessionConfig.getCurrentSerie();
        const currTask = apiTaskConfig.current_task;
        const currSerieName = apiTaskConfig.current_task.series?.toLowerCase();

        // find the current series and validate all the previous series tasks
        while (sessionConfig.getCurrentSerie()?.name?.toLowerCase() !== currSerieName) {
            // force check all tasks of the current series before the selection of the next one
            (sessionConfig.getCurrentSerie()?.tasks ?? []).forEach((task) => task.forceCheck());
            currSerie = sessionConfig.selectNextSerie();
        }

        // find the current task and validate all the previous task in the current serie
        while (currSerie?.getCurrentTask()?.name !== currTask.name) {
            currSerie?.getCurrentTask()?.forceCheck();
            currSerie?.selectNextTask();
        }

        return sessionConfig;
    }

    function resetSession(): void {
        // terminate correctly the current transaction before we load another session
        if (currentTransaction.value) {
            addLabels({ session: `${session.value?.id}` });
            endTransaction();
        }

        const newSession = new Session('', [new Serie([new BasicTask('pem-ide', 'LOGIN page')], 'PEM')]);
        session.value = newSession;
        taskConfig.value = null;
        currentSessionStatus.value = null;

        // Disable the metrics composable
        setObservedSessionId(undefined);
        setStudentId(undefined);

        // The header audio plays belong to the session that just ended.
        clearAllHeaderAudioProgress();

        setCurrentTaskConfig(null);

        loadCurrentTaskAndSerie();

        router.push({ name: 'homepage' });
    }

    function joinSession(studentCode: string): Promise<ApiTaskConfig> {
        if (process.env.VUE_APP_FAKE_SESSION === 'true') {
            console.warn('Fake session mode: the session is not loaded from the API');
            return new Promise<ApiTaskConfig>((resolve) => {
                const fakeTaskConfig = fakeConfig.config();
                resolve(fakeTaskConfig);
                console.log(fakeTaskConfig.session_id);
            }).then((fakeDataConfig) => {
                taskConfig.value = fakeDataConfig;
                session.value = mapApiTaskConfigToSessionModel(fakeDataConfig);

                setObservedSessionId(session.value.id);
                setStudentId(taskConfig.value.student_id);

                // select the PEM pem-ide task by default
                currentSerie.value = session.value?.series[0] as Serie;
                currentTask.value = session.value?.series[0]?.tasks[0] as TaskModel;

                if (!currentSerie.value || !currentTask.value) {
                    throw new Error('The pem-ide task cannot be selected after the login');
                }

                return fakeDataConfig;
            });
        }
        return axios
            .post(`session/join`, {
                code: studentCode,
            })
            .then((response) => {
                const responseData = response.data as ApiTaskConfig;

                // Legacy task config
                taskConfig.value = responseData;
                session.value = mapApiTaskConfigToSessionModel(responseData);
                currentSessionStatus.value = responseData.session_status;

                // Ensures that all the session events in the local storage are sent to the server
                flushAllSessionEvents();

                // Configure the metrics composable with the correct session ID and student ID
                setObservedSessionId(session.value.id);
                setStudentId(taskConfig.value.student_id);

                // A student who reloads joins this same session again: only other sessions' plays go.
                clearHeaderAudioProgressOfOtherSessions(session.value.id);

                // V2 Task config management is done in the useTaskProgress composable
                setCurrentTaskConfig(responseData);

                dispatchSessionEvents([createSessionEvent(SessionEventScope.SESSION, SessionEventType.SESSION_START)]);

                // select the PEM pem-ide task by default
                currentSerie.value = session.value?.series[0] as Serie;
                currentTask.value = session.value?.series[0]?.tasks[0] as TaskModel;

                if (!currentSerie.value || !currentTask.value) {
                    throw new Error('The pem-ide task cannot be selected after the login');
                }

                return responseData;
            });
    }

    /**
     * This function allows sending an answer to the server.
     * It can be used to send task, step or legacy task answers.
     * Every time a task answer is sent, the task progress is updated, and session events are flushed.
     */
    function sendAnswer(
        seriesCode: string,
        task: string | number,
        content?: string,
        file?: Blob,
        stepCode?: string,
        nextStepCode?: string,
        isLegacy?: boolean,
        timeoutMs?: number
    ): Promise<AxiosResponse | boolean> {
        /* eprocom-pwa#857: a step named without a next step is the answer that ENDS the task — the
         * audio template's last step and its skip (eprocom-backend#529 §8.1). It goes to `answer`. */
        const endsTaskFromStep = !!stepCode && !nextStepCode && !isLegacy;
        const route = stepCode && !endsTaskFromStep ? 'answer/step' : 'answer';

        const formData = new FormData();

        formData.append('series_code', seriesCode);
        /* Real task_id (versioned/editable tasks) or legacy task_code string (PEM/END/old MAT-DIV-style
         * system tasks, which have no row in the tasks table). */
        if (typeof task === 'number') {
            formData.append('task_id', String(task));
        } else {
            formData.append('task_code', task);
        }

        if (stepCode && nextStepCode) {
            formData.append('step_code', stepCode);
            formData.append('next_step_code', nextStepCode);
        } else if (endsTaskFromStep && stepCode) {
            formData.append('step_code', stepCode);
        } else if (!isLegacy && nextStepCode) {
            throw new Error('The next step code is required when the step code is provided');
        }

        if (file) {
            formData.append('file', file);
        }

        // a content is required if no file is provided
        if (content || !file) {
            formData.append('content', content ?? '');
        }

        let response: Promise<AxiosResponse | boolean> = axios.post(route, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            ...(timeoutMs !== undefined ? { timeout: timeoutMs } : {}),
        });

        if (process.env.VUE_APP_FAKE_SESSION === 'true') {
            console.warn('Fake session mode: the answer is not sent to the server');
            response = response.catch(() => false);
        } else if (endsTaskFromStep) {
            /* One promise for the upload AND the next-task pull: the caller keeps a single loader over both.
             * The pull gets what is left of the timeout, and a pull that fails rejects the whole call so the
             * student can send again (the backend accepts a resend, eprocom-backend#529 §8.1). */
            const sentAt = Date.now();
            response = response.then(async (uploaded) => {
                const pullTimeoutMs =
                    timeoutMs !== undefined
                        ? Math.max(timeoutMs - (Date.now() - sentAt), MIN_NEXT_TASK_PULL_TIMEOUT_MS)
                        : undefined;
                const nextTask = await getTaskToDo(pullTimeoutMs);
                if (!nextTask) {
                    throw new Error('The next task could not be loaded');
                }
                return uploaded;
            });
        } else if (!stepCode) {
            // Check the next task to do if the answer is not a step answer
            response.then(() => getTaskToDo());
        }

        // Always send all the session events when switching between tasks or steps
        forceFlushSessionEvents();

        return response;
    }

    /**
     * Sends an answer for a versioned/editable task, identified by its real task_id (P7 lot F) — the
     * series_code is provided explicitly by the caller (read from the task config) instead of being
     * parsed out of a legacy id string.
     */
    function sendAnswerWithTaskId(
        taskId: number,
        seriesCode: string,
        content?: string,
        file?: Blob,
        stepCode?: string,
        nextStepCode?: string,
        timeoutMs?: number
    ): Promise<AxiosResponse | boolean> {
        return sendAnswer(seriesCode, taskId, content, file, stepCode, nextStepCode, false, timeoutMs);
    }

    /**
     * The goal of this method is to centralize the listeners registration for the presence channel for the session
     */
    function registerSessionChannelListeners(channel: PresenceChannel): void {
        channel.listen('.updated', (data) => {
            currentSessionStatus.value = data.status;
        });
        channel.listen('.progress.updated', ({ model: progress }: { model: Progress }) => {
            if (progress.student_id === taskConfig.value?.student_id) {
                if (
                    progress.task_code === currentTask.value?.name &&
                    progress.series_code === currentSerie.value?.name
                ) {
                    currentProgressStatus.value = progress.status;
                }
            }
        });

        const studentId = taskConfig.value?.student_id ?? null;
        if (!studentId) {
            throw new Error(
                'The student ID is not defined in the task config. Cannot define the listener to stop the student session.'
            );
        }
        channel.listen(`.stop.student.${studentId}`, async () => {
            dispatchSessionEvents(
                [createSessionEvent(SessionEventScope.SESSION, SessionEventType.SESSION_FORCE_END)],
                true
            );
            currentSessionStatus.value = SessionStatus.TIMED_OUT;
            await flushSessionEventsIfCurrentSessionExist();
            resetSession();
        });
    }

    return {
        currentSerie,
        currentTask,
        session,
        currentSessionStatus,
        currentProgressStatus,
        taskConfig,
        loadCurrentTaskAndSerie,
        joinSession,
        sendAnswer,
        sendAnswerWithTaskId,
        resetSession,
        registerSessionChannelListeners,
    };
}
